import clsx from "clsx";
import { ChevronRight, type LucideIcon, X } from "lucide-react";
import React, { useId } from "react";

type FollowUpActionProps = {
  icon: LucideIcon;
  label: string;
  /** One line on what the action does; read as the button's description, not its name. */
  hint: string;
  onClick: () => void;
  /** Offers an X beside the card for someone who does not want this next step. */
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
};

/**
 * The next step offered once an answer is on screen — keep asking, or keep
 * tracking. A card rather than a link, so it reads as somewhere to go next
 * instead of a footnote to the answer.
 */
const FollowUpAction = ({
  icon: Icon,
  label,
  hint,
  onClick,
  onDismiss,
  dismissLabel,
  className,
}: FollowUpActionProps) => {
  const id = useId();
  // Tinted like the key insight, so the next step stands apart from the answer around it.
  const action = (
    <button
      type="button"
      onClick={onClick}
      aria-labelledby={`${id}-label`}
      aria-describedby={`${id}-hint`}
      className={clsx(
        "group flex w-full max-w-md shrink-0 items-center gap-3 rounded-xl border border-insight-line bg-insight-soft px-3 py-2.5 text-left transition hover:border-insight/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-insight/40",
        onDismiss ? "min-w-0 flex-1" : className,
      )}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-insight/10 text-insight">
        <Icon size={16} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span id={`${id}-label`} className="block text-sm font-medium text-text">
          {label}
        </span>
        <span id={`${id}-hint`} className="block text-xs leading-4 text-text-dim">
          {hint}
        </span>
      </span>
      <ChevronRight
        size={16}
        aria-hidden="true"
        className="shrink-0 text-text-dim transition group-hover:translate-x-0.5 group-hover:text-insight"
      />
    </button>
  );
  if (!onDismiss) return action;
  return (
    <div className={clsx("flex items-center gap-1", className)}>
      {action}
      <button
        type="button"
        onClick={onDismiss}
        aria-label={dismissLabel}
        title={dismissLabel}
        className="shrink-0 rounded-md p-1.5 text-text-dim transition hover:bg-muted-surface hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-insight/40"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
};

export default FollowUpAction;
